import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { AuditoriaAccion, AuditoriaModulo, AuditoriaPantalla, AuditoriaResultado } from '@enum/auditoria/auditoria-keys.enum';

const URL_BACKEND = environment.urlBackend;

const SESSION_TRACE_KEY = 'inscripcion_os_trace';
const LOCAL_TRACES_KEY = 'inscripcion_os_traces';
const MAX_TRACES_LOCALES = 10;

export interface InscripcionTraceEvento {
    ts: string;
    accion: string;
    paso?: string;
    resultado: AuditoriaResultado;
    status?: number;
    duracionMs?: number;
    traceId: string;
    solicitudId?: string | null;
    usuarioId?: string | null;
    email?: string | null;
    payload?: Record<string, any>;
    metadata?: Record<string, any>;
}

interface InscripcionTraceIntento {
    traceId: string;
    solicitudId: string | null;
    usuarioId: string | null;
    email: string | null;
    startedAt: string;
    events: InscripcionTraceEvento[];
}

interface LogOpts {
    paso?: string;
    status?: number;
    duracionMs?: number;
    payload?: Record<string, any>;
    metadata?: Record<string, any>;
    resultado?: AuditoriaResultado;
}

/**
 * Traza el flujo de inscripción O.S. de punta a punta.
 *
 * - Genera un `traceId` por intento (persistido en sessionStorage: sobrevive a
 *   recargas) y propaga la FK `solicitudId`/`usuarioId` a todos los eventos una
 *   vez conocidas.
 * - Guarda un buffer local (localStorage) de los últimos intentos, útil cuando
 *   el request al backend nunca llega.
 * - Hace flush best-effort al endpoint público de auditoría de inscripciones;
 *   jamás rompe el flujo del usuario.
 */
@Injectable({ providedIn: 'root' })
export class InscripcionTraceService {

    constructor(private httpClient: HttpClient) {}

    /** Arranca (o reanuda) un intento. Devuelve el traceId vigente. */
    startTrace(): string {
        const actual = this.getIntentoActual();
        if (actual?.traceId) {
            return actual.traceId;
        }
        return this.nuevoIntento();
    }

    /** Crea un intento nuevo (descarta el contexto vigente, conserva el histórico). */
    nuevoIntento(): string {
        const intento: InscripcionTraceIntento = {
            traceId: this.uuid(),
            solicitudId: null,
            usuarioId: null,
            email: null,
            startedAt: new Date().toISOString(),
            events: [],
        };
        this.setIntentoActual(intento);
        this.upsertIntentoLocal(intento);
        return intento.traceId;
    }

    setUsuarioId(usuarioId: string | null): void {
        this.patchIntento({ usuarioId });
    }

    setSolicitudId(solicitudId: string | null): void {
        this.patchIntento({ solicitudId });
    }

    setEmail(email: string | null): void {
        this.patchIntento({ email });
    }

    getTraceId(): string {
        return this.getIntentoActual()?.traceId || '';
    }

    /** Código corto para mostrar al usuario en modales de error. */
    getReferencia(): string {
        const traceId = this.getTraceId();
        return traceId ? traceId.slice(0, 8).toUpperCase() : '';
    }

    logStep(accion: AuditoriaAccion | string, opts: LogOpts = {}): void {
        this.log(accion, { ...opts, resultado: opts.resultado || AuditoriaResultado.EXITO });
    }

    logError(accion: AuditoriaAccion | string, error: any, opts: LogOpts = {}): void {
        const mensaje = this.mensajeDeError(error);
        this.log(accion, {
            ...opts,
            resultado: AuditoriaResultado.ERROR,
            status: opts.status ?? (typeof error?.status === 'number' ? error.status : undefined),
            payload: { ...(opts.payload || {}), error: mensaje },
        });
    }

    /** Intentos guardados localmente (más reciente primero). */
    getIntentosLocales(): InscripcionTraceIntento[] {
        try {
            const raw = localStorage.getItem(LOCAL_TRACES_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }

    clearLocal(): void {
        localStorage.removeItem(LOCAL_TRACES_KEY);
        sessionStorage.removeItem(SESSION_TRACE_KEY);
    }

    private log(accion: AuditoriaAccion | string, opts: LogOpts): void {
        const intento = this.getIntentoActual();
        if (!intento) {
            return;
        }

        const evento: InscripcionTraceEvento = {
            ts: new Date().toISOString(),
            accion: accion as string,
            paso: opts.paso,
            resultado: opts.resultado || AuditoriaResultado.EXITO,
            status: opts.status,
            duracionMs: opts.duracionMs,
            traceId: intento.traceId,
            solicitudId: intento.solicitudId,
            usuarioId: intento.usuarioId,
            email: intento.email,
            payload: opts.payload,
            metadata: { ...this.metadataBase(), ...(opts.metadata || {}) },
        };

        intento.events.push(evento);
        this.setIntentoActual(intento);
        this.upsertIntentoLocal(intento);
        this.flush(evento);
    }

    private flush(evento: InscripcionTraceEvento): void {
        const body = {
            accion: evento.accion,
            modulo: AuditoriaModulo.INSCRIPCION_OS,
            pantalla: AuditoriaPantalla.INSCRIPCION_OS,
            ruta: '/inscripcion-os',
            traceId: evento.traceId,
            solicitudId: evento.solicitudId || undefined,
            usuarioId: evento.usuarioId || undefined,
            resultado: evento.resultado,
            status: evento.status,
            payload: {
                ...(evento.payload || {}),
                paso: evento.paso,
                duracionMs: evento.duracionMs,
                email: evento.email || undefined,
            },
            metadata: evento.metadata,
        };

        this.httpClient
            .post(`${URL_BACKEND}/signo-auditoria/inscripcion`, body)
            .subscribe({
                next: () => {},
                error: (err) => console.error('[InscripcionTrace] No se pudo enviar el evento:', err),
            });
    }

    private metadataBase(): Record<string, any> {
        const nav: any = navigator as any;
        const conexion = nav.connection || nav.mozConnection || nav.webkitConnection;
        return {
            userAgent: nav.userAgent,
            online: nav.onLine,
            idioma: nav.language,
            conexion: conexion
                ? { tipo: conexion.effectiveType, downlink: conexion.downlink, rtt: conexion.rtt }
                : undefined,
            origen: window.location.origin,
        };
    }

    private mensajeDeError(error: any): string {
        if (!error) {
            return 'Error desconocido';
        }
        if (typeof error === 'string') {
            return error;
        }
        return error.message || error.statusText || JSON.stringify(error)?.slice(0, 300) || 'Error desconocido';
    }

    private getIntentoActual(): InscripcionTraceIntento | null {
        try {
            const raw = sessionStorage.getItem(SESSION_TRACE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    private setIntentoActual(intento: InscripcionTraceIntento): void {
        try {
            sessionStorage.setItem(SESSION_TRACE_KEY, JSON.stringify(intento));
        } catch {}
    }

    private patchIntento(patch: Partial<InscripcionTraceIntento>): void {
        const intento = this.getIntentoActual();
        if (!intento) {
            return;
        }
        const actualizado = { ...intento, ...patch };
        this.setIntentoActual(actualizado);
        this.upsertIntentoLocal(actualizado);
    }

    private upsertIntentoLocal(intento: InscripcionTraceIntento): void {
        try {
            const intentos = this.getIntentosLocales().filter((i) => i.traceId !== intento.traceId);
            intentos.unshift(intento);
            localStorage.setItem(LOCAL_TRACES_KEY, JSON.stringify(intentos.slice(0, MAX_TRACES_LOCALES)));
        } catch {}
    }

    private uuid(): string {
        const c: any = window.crypto || (window as any).msCrypto;
        if (c?.randomUUID) {
            return c.randomUUID();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
            const r = Math.floor(Math.random() * 16);
            const v = ch === 'x' ? r : (r % 4) + 8;
            return v.toString(16);
        });
    }
}
